<template>
  <div class="app-modal" :id="modalId">
    <transition mode="out-in"
                enter-active-class="animate__animated animate__fadeIn"
                leave-active-class="animate__animated animate__fadeOut">
      <div class="app-modal-backdrop" :class="{'docPrint': !extendedModal }" ref="alertModal" v-if="showModal && !isEsc" >
        <div class="app-modal-content" :class="[{ '__large': isSizedUp }, {'__overflow': isOverflow}]" role="dialog" ref="modalContent"
             aria-labelledby="modalHeader">
          <header>
            <h1 id="modalHeader"><!-- title: should describe modal -->
              <slot name="heading"></slot>
            </h1>
          </header>
          <main>
            <slot name="content"></slot><!-- main content: any visible HTML elements -->
          </main>
          <footer><!-- buttons, links, etc. -->
            <slot name="footing"></slot>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'BaseModal',
  props: {
    modalId: {
      type: String,
      required: true,
    },
    isSizedUp: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOverflow: {
      type: Boolean,
      required: false,
      default: false,
    },
    showModal: {
      type: Boolean,
      required: true,
    },
    allowEsc: {
      type: Boolean,
      required: false,
      default: true,
    },
    extendedModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modal: undefined,
      firstFocusableElement: undefined,
      focusableContent: [],
      lastFocusableElement: undefined,
      isEsc: false,
    };
  },
  watch: {
    showModal() {
      if (this.showModal) {
        this.$nextTick(() => {
          this.init();
        });
      }
    }
  },
  destroyed() {
    document.removeEventListener('keydown', this.bindKeys);
  },
  methods: {
    init() {
      // add all the elements inside modal which you want to make focusable
      // e.g. 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      this.focusableElements = 'button, input';
      this.modal = document.querySelector(`#${this.modalId}`);
      this.firstFocusableElement = this.modal.querySelectorAll(this.focusableElements)[0];
      this.focusableContent = this.modal.querySelectorAll(this.focusableElements);
      this.lastFocusableElement = this.focusableContent[this.focusableContent.length - 1];

      if (this.firstFocusableElement && !this.isOverflow) {
        this.firstFocusableElement.focus();
      }
    },
    bindKeys(e) {
      if (this.allowEsc) {
        // check for Esc (close modal) and Tab/Shift-Tab (trap focus) key presses
        const isEscPressed = e.key === 'Escape' || e.keyCode === 27;
        if (isEscPressed) {
          this.isEsc = true;
        }
      }

      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
      if (!isTabPressed) {
        return;
      }

      // shift (for Shift + Tab)
      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusableElement) {
          // cycle backward to last focusable
          this.lastFocusableElement.focus();
          e.preventDefault();
        }
      } else { // tab
        if (document.activeElement === this.lastFocusableElement) {
          // cycle forward to first focusable
          this.firstFocusableElement.focus(); // add focus for the first focusable element
          e.preventDefault();
        }
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@import "~@ridmv/web-themes/styles/modal";
.app-modal {
  .docPrint{
    z-index: 101 !important;
  }
  .app-modal-content {
    &.__overflow {      // add vertical scroll within modal if content gets too long
      height: 100%;
      overflow-y: auto;
      width: 100%;

      @media print {
        width: 220% !important;
        overflow-y: hidden;
        height: 115% !important;
      }
    }
  }
}

@media only screen and (max-width: 599px) {
  .app-modal {
  .app-modal-content {
    &.__overflow {      // add vertical scroll within modal if content gets too long
      height: 100vh;
      overflow-y: auto;
      width: 100%;

      @media print {
        width: 220% !important;
        overflow-y: hidden;
        height: 115% !important;
      }
    }
  }
}
}
</style>
